import type { DeckStats, IceType, Tool } from '~/types/game';

/** Baseline deck stats for a new operator (design section 4). These double as
 *  the loadout deployment budget. Tunable. */
export const STARTER_DECK_STATS: DeckStats = {
  processing: 40,
  intrusion: 30,
  stealth: 30,
};

/** The counter verb shown for a tool's `counters` type — the action it performs
 *  against the matching ICE type. */
export const COUNTER_VERB: Record<IceType, string> = {
  WALL: 'BREACH',
  CIPHER: 'DECRYPT',
  DAEMON: 'PURGE',
};

/** The full tool catalog.
 *
 *  Capacity scheme: `cost` is `(processing, intrusion, stealth)` and ALL three
 *  deck stats matter — processing = compute weight, intrusion = offensive
 *  footprint, stealth = noise — so no stat is dead weight when building a
 *  loadout. Two tools per counter type give a cheap/weak vs expensive/strong
 *  tradeoff. Six tools span the three counter types with real choice. All
 *  numbers are tunable balancing values.
 *
 *  Balance pass (prompt 18): the three CHEAP starter counters (probe/slip_key/
 *  null_script) were raised so a realistic starter loadout — which can only
 *  field ONE matched tool (probe+ram exceed the intrusion budget) plus
 *  mismatched filler — clears tier-1 cleanly and reaches PARTIAL on tier-2/MED,
 *  while tier-3/4 stay unbeatable (defense untouched). probe 10→15,
 *  slip_key 12→16, null_script 14→17. The heavy tools (ram/sieve/revenant) and
 *  all costs are unchanged, so they remain the clear upgrade path and the deck
 *  capacity behavior is identical. */
export const TOOLS: Tool[] = [
  { id: 'probe', name: 'PROBE', counters: 'WALL', cost: { processing: 8, intrusion: 10, stealth: 4 }, strength: 15, desc: 'light perimeter breach' },
  { id: 'ram', name: 'BATTERING_RAM', counters: 'WALL', cost: { processing: 20, intrusion: 22, stealth: 14 }, strength: 26, desc: 'heavy breach · loud' },
  { id: 'slipkey', name: 'SLIP_KEY', counters: 'CIPHER', cost: { processing: 12, intrusion: 8, stealth: 6 }, strength: 16, desc: 'quick cipher slip' },
  { id: 'sieve', name: 'QUANTUM_SIEVE', counters: 'CIPHER', cost: { processing: 24, intrusion: 14, stealth: 10 }, strength: 28, desc: 'deep decrypt · costly' },
  { id: 'nullscr', name: 'NULL_SCRIPT', counters: 'DAEMON', cost: { processing: 14, intrusion: 12, stealth: 8 }, strength: 17, desc: 'daemon purge routine' },
  { id: 'revenant', name: 'REVENANT', counters: 'DAEMON', cost: { processing: 18, intrusion: 20, stealth: 18 }, strength: 24, desc: 'persistent purge · aggressive' },
];

const TOOL_BY_ID = new Map(TOOLS.map((t) => [t.id, t]));

export function toolById(id: string): Tool | undefined {
  return TOOL_BY_ID.get(id);
}

/** What a new operator owns at start (design section 4): one cheap counter per
 *  ICE type (probe/slipkey/nullscr) plus the heavy WALL option (ram) for a
 *  cost/strength choice — enough to assemble a valid low-tier loadout. The
 *  remaining tools are acquired at shops (step 5). */
export const STARTER_OWNED_TOOL_IDS = ['probe', 'ram', 'slipkey', 'nullscr'];
